import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Edit2,
  Plus,
  Phone,
  Mail,
  MapPin,
  Building2,
  Briefcase,
  Clock,
  CheckCircle2,
  Calendar,
  IndianRupee
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useClient } from '../lib/queries';
import Shell from '../components/Shell';
import { ClientDrawer } from './Clients';
import { ProjectDrawer } from './Projects';
import {
  StatusChip,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  initials,
} from '../components/ui';

export default function ClientDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useClient(id);
  const [editing, setEditing] = useState(false);
  const [addingProject, setAddingProject] = useState(false);

  return (
    <Shell
      title={data?.name ?? 'Client Profile'}
      crumb="Clients"
      actions={
        data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-auto btn-sm"
              onClick={() => setEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Edit2 size={13} />
              <span>Edit Profile</span>
            </button>
            <button
              className="btn btn-primary btn-auto btn-sm"
              onClick={() => setAddingProject(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Plus size={14} strokeWidth={2.2} />
              <span>Add Project</span>
            </button>
          </div>
        )
      }
    >
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/clients')}
      >
        <ArrowLeft size={14} />
        <span>Back to Clients</span>
      </button>

      {isLoading ? (
        <div className="card"><TableSkeleton rows={4} cols={4} /></div>
      ) : error ? (
        <div className="card"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : !data ? (
        <div className="card"><EmptyState icon="users" title="Client not found" /></div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Client Identity Card */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div
                className="avatar-sq"
                style={{
                  width: 52,
                  height: 52,
                  fontSize: 18,
                  borderRadius: 'var(--radius-sm)',
                  background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                  color: '#2563eb',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'var(--shadow-xs)',
                  flexShrink: 0,
                }}
              >
                {initials(data.name)}
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{data.name}</h2>
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 10,
                    fontSize: 13,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Phone size={13} style={{ color: 'var(--text-faint)' }} />
                    <span className="num">{data.phone}</span>
                  </span>

                  {data.email && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Mail size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{data.email}</span>
                    </span>
                  )}

                  {data.city && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <MapPin size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{data.city}</span>
                    </span>
                  )}
                </div>

                {data.address && (
                  <div
                    style={{
                      fontSize: 12.5,
                      color: 'var(--text-faint)',
                      marginTop: 10,
                      maxWidth: 720,
                      lineHeight: 1.5,
                      background: 'var(--surface-2)',
                      padding: '8px 12px',
                      borderRadius: 'var(--radius-xs)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    {data.address}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Project Summary Metrics */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div className="lbl">
                <span>Total Projects</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={14} />
                </div>
              </div>
              <div className="val">{data.totalProjects}</div>
              <div className="sub">contracted works</div>
            </div>

            <div className="kpi">
              <div className="lbl">
                <span>Ongoing Construction</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Clock size={14} />
                </div>
              </div>
              <div className="val" style={{ color: '#059669' }}>
                {data.ongoingProjects}
              </div>
              <div className="sub">active building sites</div>
            </div>

            <div className="kpi">
              <div className="lbl">
                <span>Completed Sites</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f8fafc', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={14} />
                </div>
              </div>
              <div className="val">{data.completedProjects}</div>
              <div className="sub">delivered to client</div>
            </div>
          </div>

          {/* Associated Projects Table */}
          <div className="card">
            <div
              className="card-head"
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              <div>
                <h3 style={{ fontSize: 15, margin: 0, fontWeight: 700 }}>Client Project Portfolio</h3>
                <div className="desc" style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 2 }}>
                  Contract values, payment collections, and pending dues for each project
                </div>
              </div>
            </div>

            <div className="table-wrap">
              {!data.projects.length ? (
                <EmptyState
                  icon="briefcase"
                  title="No projects linked yet"
                  hint="Create a project for this client using the button above."
                />
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', paddingLeft: 24 }}>Project Name</th>
                      <th style={{ textAlign: 'left' }}>Start Date</th>
                      <th style={{ textAlign: 'left' }}>Contract Value</th>
                      <th style={{ textAlign: 'left' }}>Collections Received</th>
                      <th style={{ textAlign: 'left' }}>Pending Balance</th>
                      <th style={{ textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((p) => (
                      <tr
                        key={p.id}
                        className="clickable"
                        onClick={() => navigate(`/projects/${p.id}`)}
                      >
                        <td className="name" style={{ textAlign: 'left', paddingLeft: 24 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          {p.startDate ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                              <Calendar size={11} style={{ color: 'var(--text-faint)' }} />
                              <span>{formatDate(p.startDate)}</span>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="num" style={{ textAlign: 'left', fontWeight: 500 }}>
                          {formatMoney(p.valuePaise)}
                        </td>
                        <td className="num" style={{ textAlign: 'left', color: '#059669', fontWeight: 600 }}>
                          {formatMoney(p.paidPaise)}
                        </td>
                        <td
                          className="num"
                          style={{
                            textAlign: 'left',
                            color: p.pendingPaise > 0 ? '#ea580c' : 'var(--text-faint)',
                            fontWeight: p.pendingPaise > 0 ? 600 : 400,
                          }}
                        >
                          {formatMoney(p.pendingPaise)}
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          <StatusChip value={p.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ textAlign: 'left', paddingLeft: 24, fontWeight: 700 }}>Total Portfolio</td>
                      <td />
                      <td className="num" style={{ textAlign: 'left', fontWeight: 700 }}>
                        {formatMoney(data.projects.reduce((s, p) => s + p.valuePaise, 0))}
                      </td>
                      <td className="num" style={{ textAlign: 'left', fontWeight: 700, color: '#059669' }}>
                        {formatMoney(data.projects.reduce((s, p) => s + p.paidPaise, 0))}
                      </td>
                      <td className="num" style={{ textAlign: 'left', fontWeight: 700, color: '#ea580c' }}>
                        {formatMoney(data.projects.reduce((s, p) => s + p.pendingPaise, 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {editing && data && <ClientDrawer existing={data} onClose={() => setEditing(false)} />}

      {addingProject && (
        <ProjectDrawer defaultClientId={id} onClose={() => setAddingProject(false)} />
      )}
    </Shell>
  );
}

